import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, apiErr } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import GoogleButton from "@/components/GoogleButton";
import { GraduationCap, Loader2, MailWarning } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setUnverified(false);
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.role === "recruiter" ? "/candidates" : "/tracks");
    } catch (err) {
      setError(apiErr(err));
      if (err?.response?.status === 403) setUnverified(true);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success("Verification email sent. Please check your inbox.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-lg bg-[#6366F1] flex items-center justify-center">
            <GraduationCap size={22} className="text-white" />
          </div>
          <span className="font-extrabold tracking-tight text-xl text-white">Enjyme</span>
        </Link>
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-xl">
          <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Welcome back</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Sign in to your Career GPS.</p>

          <GoogleButton />

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Password</label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-[#6366F1] hover:underline">Forgot password?</Link>
              </div>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
            {unverified && (
              <div data-testid="login-unverified" className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="flex items-center gap-2 text-sm text-amber-800 font-medium"><MailWarning size={16} /> Email not verified</p>
                <button type="button" data-testid="resend-verification-btn" onClick={resend} disabled={resending || !email}
                  className="mt-2 text-sm font-semibold text-[#6366F1] hover:underline disabled:opacity-50">
                  {resending ? "Sending…" : "Resend verification email"}
                </button>
              </div>
            )}
            <button data-testid="login-submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60">
              {loading && <Loader2 size={16} className="animate-spin" />} Sign in
            </button>
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            New here? <Link to="/register" className="text-[#6366F1] font-semibold hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
